/**
 * Pure deal calculators — extracted from the mockup's `renderAll`,
 * `computeStep2`, and `recalcCashflowNet`. All functions are deterministic,
 * side-effect free, and operate on plain numbers (no DB / React deps).
 *
 * Conventions:
 *   - Percent inputs are passed as percent points (e.g. 75 means 75%, not 0.75).
 *   - Money inputs are in dollars (no cents).
 *   - Annual rates are in percent points; the functions divide by 12 internally.
 *   - Returned monetary values are unrounded; format with `fmtMoney` at the edge.
 */
import type { LoanInputs } from "@/types/pipeline";

/* ────────────────────────────────────────────────────────────────────────── *
 * Term resolution
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve a loan's term to MONTHS — the canonical unit for every loan
 * calculation. Prefers the explicit `term_months` (source of truth, supports
 * mixed terms like 30y 6m = 366); falls back to the legacy slot-implied `term`
 * (months for hard money, years for long-term) for un-migrated blobs.
 *
 * `slotDefault` names how the legacy `term` should be read for this slot — pass
 * "months" for hard-money slots and "years" for long-term/refi/conventional
 * slots. It is ignored once `term_months` is present.
 */
export function termInMonths(
  loan: Pick<LoanInputs, "term" | "term_months">,
  slotDefault: "months" | "years",
): number {
  const explicit = loan.term_months;
  if (explicit != null && Number.isFinite(explicit)) return Math.max(0, explicit);
  const legacy = Number(loan.term) || 0;
  return slotDefault === "years" ? legacy * 12 : legacy;
}

/** Split a month count into whole years + leftover months (for years-mode entry). */
export function monthsToYearsMonths(months: number): {
  years: number;
  months: number;
} {
  const total = Math.max(0, Math.round(Number(months) || 0));
  return { years: Math.floor(total / 12), months: total % 12 };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Formatting helpers
 * ────────────────────────────────────────────────────────────────────────── */

export function parseMoney(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Money formatter. Whole values stay clean (`$1,234`); values with a
 * fractional part show 2 decimals (`$1,234.50`, `$1,234.57`). Rounds to 2
 * decimals first, so unrounded calculator floats never leak extra digits.
 */
export function fmtMoney(value: number): string {
  const rounded = round2(value || 0);
  const digits = Number.isInteger(rounded) ? 0 : 2;
  return (
    "$" +
    rounded.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  );
}

export function fmtPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Loan math
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Standard amortized EMI (Equated Monthly Installment).
 * EMI = P * r * (1+r)^n / ((1+r)^n - 1)
 * Where P = loan amount, r = monthly rate, n = term in months.
 * Falls back to straight-line when rate is 0.
 */
export function amortizedEmi(
  loanAmount: number,
  annualRatePct: number,
  termMonths: number,
): number {
  if (termMonths <= 0 || loanAmount <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return loanAmount / termMonths;
  const f = Math.pow(1 + r, termMonths);
  return (loanAmount * r * f) / (f - 1);
}

/** Interest-only monthly payment on a fixed-principal loan. */
export function interestOnlyMonthly(
  loanAmount: number,
  annualRatePct: number,
): number {
  if (loanAmount <= 0 || annualRatePct <= 0) return 0;
  return loanAmount * (annualRatePct / 100 / 12);
}

/** Loan principal funded by lender (LTV % of purchase). */
export function loanAmount(purchase: number, ltvPct: number): number {
  return purchase * (ltvPct / 100);
}

/** Lender origination fee in dollars (points × loan amount). */
export function originationFee(loanAmt: number, points: number): number {
  return loanAmt * (points / 100);
}

/** Down payment = purchase × (1 − LTV%). Returns full price when ltvPct is 0 (no loan). */
export function downPayment(purchase: number, ltvPct: number): number {
  const pct = Math.max(0, Math.min(100, 100 - ltvPct));
  return purchase * (pct / 100);
}

/** Closing cost in dollars. */
export function closingCost(purchase: number, closingPct: number): number {
  return purchase * (closingPct / 100);
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Deal metrics
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Max Allowable Offer (Fix-and-Flip 70% rule, parameterized).
 * MAO = (ARV × pct) − rehab. Clamped at 0.
 */
export function mao(arv: number, rehab: number, pct: number): number {
  return Math.max(0, arv * (pct / 100) - rehab);
}

/** Round to at most 2 decimal places (e.g. 1234.5678 → 1234.57). */
export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Cap rate = NOI ÷ price (percent). */
export function capRate(noi: number, price: number): number {
  return price > 0 ? (noi / price) * 100 : 0;
}

/** Cash-on-cash return = annual cashflow ÷ cash invested (percent). */
export function cashOnCash(annualCashFlow: number, cashInvested: number): number {
  return cashInvested > 0 ? (annualCashFlow / cashInvested) * 100 : 0;
}

/** Gross Rent Multiplier = price ÷ annual gross rent. */
export function grm(price: number, annualRent: number): number {
  return annualRent > 0 ? price / annualRent : 0;
}

/** 1% rule = monthly rent ÷ price (percent). ≥ 1% is ideal. */
export function onePctRule(monthlyRent: number, price: number): number {
  return price > 0 ? (monthlyRent / price) * 100 : 0;
}

/** Debt Service Coverage Ratio = NOI ÷ debt service. */
export function dscr(noi: number, debtService: number): number {
  return debtService > 0 ? noi / debtService : 0;
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Aggregate calculators
 * ────────────────────────────────────────────────────────────────────────── */

export interface CashflowInputs {
  gross: number;
  other?: number;
  vacancy?: number;
  mgmt?: number;
  taxes?: number;
  insurance?: number;
  maint?: number;
  capex?: number;
  hoa?: number;
  utilities?: number;
  mortgage?: number;
}

export interface CashflowResult {
  /** Effective Gross Income = gross + other. */
  egi: number;
  /** Sum of all operating expense lines (excl. debt service). */
  opex: number;
  /** Net Operating Income = EGI − opex. */
  noi: number;
  /** Monthly net cashflow = NOI − mortgage. */
  monthlyNet: number;
  /** Monthly net × 12. */
  annualNet: number;
  /** Net ÷ EGI (percent). */
  margin: number;
  /** NOI ÷ mortgage. */
  dscr: number;
}

/** Standard monthly cashflow / NOI / DSCR rollup. All inputs are monthly $. */
export function computeMonthlyCashflow(input: CashflowInputs): CashflowResult {
  const gross = input.gross ?? 0;
  const other = input.other ?? 0;
  const egi = gross + other;
  const opex =
    (input.vacancy ?? 0) +
    (input.mgmt ?? 0) +
    (input.taxes ?? 0) +
    (input.insurance ?? 0) +
    (input.maint ?? 0) +
    (input.capex ?? 0) +
    (input.hoa ?? 0) +
    (input.utilities ?? 0);
  const noi = egi - opex;
  const mortgage = input.mortgage ?? 0;
  const monthlyNet = noi - mortgage;
  return {
    egi,
    opex,
    noi,
    monthlyNet,
    annualNet: monthlyNet * 12,
    margin: egi > 0 ? (monthlyNet / egi) * 100 : 0,
    dscr: dscr(noi, mortgage),
  };
}

export interface FlipResult {
  /** Total holding cost across `holdMonths`. */
  holding: number;
  /** Selling cost (commission, transfer tax, closing). */
  selling: number;
  /** All-in costs: purchase + rehab + closing + origination + holding + selling. */
  allIn: number;
  /** Profit = ARV − all-in. */
  profit: number;
  /** Cash invested (excludes financed portion). */
  cashInvested: number;
  /** ROI = profit ÷ cash invested (percent). */
  roi: number;
  /** Profit margin = profit ÷ ARV (percent). 15%+ is healthy. */
  margin: number;
  /** Equity spread = ARV − (purchase + rehab). */
  equitySpread: number;
}

export interface FlipInputs {
  purchase: number;
  rehab: number;
  arv: number;
  closingCost: number;
  /** Hard money: loan amount funded by lender. */
  hmlLoanAmt: number;
  /** Hard money origination fee in $. */
  hmlOrigFee: number;
  /** Hard money monthly debt service (EMI or interest-only). */
  hmlMonthlyDebt: number;
  /** Carrying monthly taxes & insurance. */
  monthlyTaxes: number;
  /** Carrying monthly utilities. */
  monthlyUtilities: number;
  /** Months held before sale. */
  holdMonths: number;
  /** Selling cost as percent of ARV (commission, transfer tax, closing). */
  sellPct: number;
}

/** Flip-strategy aggregate metrics. */
export function computeFlip(input: FlipInputs): FlipResult {
  const holding =
    (input.hmlMonthlyDebt + input.monthlyTaxes + input.monthlyUtilities) *
    input.holdMonths;
  const selling = input.arv * (input.sellPct / 100);
  const allIn =
    input.purchase +
    input.rehab +
    input.closingCost +
    input.hmlOrigFee +
    holding +
    selling;
  const profit = input.arv - allIn;
  const cashInvested =
    input.purchase -
    input.hmlLoanAmt +
    input.rehab +
    input.closingCost +
    input.hmlOrigFee +
    holding;
  return {
    holding,
    selling,
    allIn,
    profit,
    cashInvested,
    roi: cashInvested > 0 ? (profit / cashInvested) * 100 : 0,
    margin: input.arv > 0 ? (profit / input.arv) * 100 : 0,
    equitySpread: input.arv - (input.purchase + input.rehab),
  };
}

export interface BrrrrRefiInputs {
  arv: number;
  /** Refi loan-to-value of ARV (percent). */
  refiLtvPct: number;
  /** Refi annual interest rate (percent). */
  refiRatePct: number;
  /** Refi term in years. */
  refiTermYears: number;
  /** Refi origination points (1 = 1%). */
  refiPoints: number;
  /** Short-term (HML) loan principal that gets paid off by refi. */
  hmlLoanAmt: number;
  /** HML monthly interest carried during the holding period. */
  hmlMonthlyInterest: number;
  /** Months held before refinance. */
  holdMonths: number;
  /** Pre-refi cash invested (purchase + rehab + closing + origination). */
  totalInDeal: number;
}

export interface BrrrrRefiResult {
  refiLoan: number;
  refiOrigFee: number;
  refiEmi: number;
  /** HML interest accrued over the holding period. */
  holdingInterest: number;
  /** Net cash out = refi loan − HML payoff − refi origination − holding interest. */
  netCashOut: number;
  /** Cash left in the deal after refi cash-out (lower is better). */
  cashLeftIn: number;
  /** Equity at refi = ARV − refi loan. */
  equityAtRefi: number;
}

/** BRRRR refinance + cash-out metrics. */
export function computeBrrrrRefi(input: BrrrrRefiInputs): BrrrrRefiResult {
  const termMonths = input.refiTermYears * 12;
  const refiLoan = input.arv * (input.refiLtvPct / 100);
  const refiEmi = amortizedEmi(refiLoan, input.refiRatePct, termMonths);
  const refiOrigFee = originationFee(refiLoan, input.refiPoints);
  const holdingInterest = input.hmlMonthlyInterest * input.holdMonths;
  const netCashOut =
    refiLoan - input.hmlLoanAmt - refiOrigFee - holdingInterest;
  const cashLeftIn = input.totalInDeal - refiLoan;
  return {
    refiLoan,
    refiOrigFee,
    refiEmi,
    holdingInterest,
    netCashOut,
    cashLeftIn,
    equityAtRefi: input.arv - refiLoan,
  };
}
